import React, { useEffect, useState } from "react";
import PageContainer from "../pages/PageContainer";
import SettingsPageHeading from "../pages/SettingsPageHeading";
import { useParams, useNavigate } from "react-router-dom";
import GameSideBar from "../Games/GameSideBar";
import { getUserPerformance } from "../../api/user";
import { performanceEmptyCopy, emptyCurveData, StartDot } from "./performanceEmpty";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { SkeletonChart } from "../Skeleton/Skeleton";
import { SectionError } from "../Skeleton/LoadError";
import useDelayedFlag from "../Skeleton/useDelayedFlag";

// Shared performance chart — rendered both in the profile Performance tab and
// inside the mobile "The Record" modal. Spec C1–C9.
const GREEN = "#35b23c";
const RED = "#ff4b4b";
const NEUTRAL = "#6b7280";

// C6: inline range row.
const RANGES = [
  { key: "1M", months: 1 },
  { key: "3M", months: 3 },
  { key: "6M", months: 6 },
  { key: "1Y", months: 12 },
  { key: "All", months: null }, // full record
];

const PerfShell = ({ embedded, gamePage, children }) =>
  embedded ? (
    <>{children}</>
  ) : (
    <PageContainer gamePage={gamePage}>{children}</PageContainer>
  );

// C7: "nice" even Y step targeting ~6 ticks (e.g. range 60 -> step 10).
const niceStep = (range) => {
  const target = (range || 1) / 6;
  const pow = Math.pow(10, Math.floor(Math.log10(target || 1)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * pow >= target) return m * pow;
  }
  return 10 * pow;
};

const fmtMonthYear = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? d
    : dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const fmtFullDate = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? d
    : dt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const PerformanceSection = (props) => {
  const { userId: paramUserId } = useParams();
  const userId = props.userId ?? paramUserId;
  const game_id = props.gameId ?? null;
  const slug = props.slug ?? null;
  const embedded = props.embedded ?? false;
  // Owner (own profile / private) vs visitor (public / "view as public") — drives
  // the empty-state wording and the owner-only CTA.
  const isSelfProfile = props.isSelfProfile ?? false;
  // Has the user placed a trade yet? Drives the two empty states: no trades →
  // "start your curve", traded but chart not built yet → "your curve is
  // building". Passed down from the profile (profile.trades_count > 0).
  const hasTraded = props.hasTraded ?? false;
  // Standalone Performance page ("The Record" chart click) passes a title; the
  // embedded profile/game usages don't, so the house-rule heading only renders
  // on the dedicated page.
  const pageTitle = props.pageTitle ?? null;
  const navigate = useNavigate();

  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed performance request gets the section error from the spec rather
  // than an empty axis that looks like a real (flat) record.
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedRange, setSelectedRange] = useState("All");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadFailed(false);
      const today = new Date();
      const cfg = RANGES.find((r) => r.key === selectedRange) || RANGES[4];
      let start;
      if (cfg.months == null) {
        start = new Date(0); // full record
      } else {
        start = new Date();
        start.setMonth(start.getMonth() - cfg.months);
      }
      try {
        // Item 16: inside a league this is the LEAGUE's series. `game_id` was
        // already accepted as a prop here and simply never used — the request
        // went out unscoped, so the Performance tab on a league profile drew
        // the member's sitewide curve.
        const res = await getUserPerformance(userId, start, today, game_id);
        const rows = Array.isArray(res?.data) ? res.data : [];
        const data = rows
          .map((item) => ({
            date: String(item.performance_updated_at).split(" ")[0],
            // Keep 2-decimal precision. Rounding to whole numbers turned small
            // real returns (e.g. 0.40%) into 0, which the empty-state guard then
            // read as "no data" → stuck on "Your curve is building".
            performance: Math.round(parseFloat(item.performance) * 100) / 100,
          }))
          .filter((d) => Number.isFinite(d.performance))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (!cancelled) setPerformanceData(data);
      } catch (error) {
        console.error("Error fetching performance data:", error);
        if (!cancelled) {
          setPerformanceData([]);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, userId, game_id, reloadKey]);

  // Show a designed empty state (not a blank chart) once loading settles. A brand
  // new account still has performance-history rows, but they sit flat at 0% until
  // the first trade — so "no points" OR "every point is 0" both mean the curve
  // hasn't started yet. (A trader with real movement has at least one non-zero
  // point and gets the chart.)
  const isEmpty =
    !loading &&
    (performanceData.length === 0 ||
      performanceData.every((d) => Number(d.performance) === 0));

  // C3: one color for the whole line, by the sign of the current (latest) return.
  const lastPerf = performanceData.length
    ? performanceData[performanceData.length - 1].performance
    : 0;
  const lineColor = lastPerf >= 0 ? GREEN : RED;

  // C7: clean, even Y ticks that always include 0.
  const vals = performanceData.map((d) => d.performance);
  const dataMin = vals.length ? Math.min(...vals, 0) : 0;
  const dataMax = vals.length ? Math.max(...vals, 0) : 50;
  const step = niceStep(dataMax - dataMin);
  const yMin = Math.floor(dataMin / step) * step;
  const yMax = Math.ceil(dataMax / step) * step;

  // C8 fix: the fill is the LINE's shadow, not the axis's. Gradient is strongest
  // AT THE LINE and fades to transparent at the 0% baseline, on BOTH sides of
  // zero. The fill spans line<->0 (baseValue={0}); dataMin/dataMax already include
  // 0, so they ARE the fill's bounding box. We place a transparent stop exactly
  // where 0% sits inside that box, so below-zero segments flip the gradient
  // automatically (strong color hugging the line, not pooled at the baseline).
  const zeroOffsetPct =
    dataMax === dataMin ? "0%" : `${(dataMax / (dataMax - dataMin)) * 100}%`;
  const fillTopOpacity = dataMax > 0 ? 0.18 : 0; // above-zero side present?
  const fillBottomOpacity = dataMin < 0 ? 0.18 : 0; // below-zero side present?
  // Tick precision follows the step (e.g. a 0.1 step → 1 decimal) so a small
  // range like 0–0.4% shows "0.1%, 0.2%…" instead of five identical "0%" labels.
  const stepStr = String(Math.round(step * 1e6) / 1e6);
  const tickDecimals = stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
  const yTicks = [];
  for (let v = yMin; v <= yMax + 1e-6; v += step)
    yTicks.push(Number(v.toFixed(tickDecimals)));

  // C7: ~6 evenly spaced X ticks (human month-year labels).
  const n = performanceData.length;
  let xTicks = [];
  if (n > 0) {
    const count = Math.min(6, n);
    const set = new Set();
    for (let i = 0; i < count; i++) {
      const idx = count === 1 ? 0 : Math.round((i * (n - 1)) / (count - 1));
      set.add(performanceData[idx].date);
    }
    xTicks = [...set];
  }

  // C5: tooltip — dark teal, date + bold signed value, no shadow.
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const v = Number(payload[0].value);
    const color = v > 0 ? GREEN : v < 0 ? RED : NEUTRAL;
    return (
      <div
        style={{
          background: "#004c50",
          borderRadius: 8,
          padding: "6px 10px",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
        }}
      >
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
          {fmtFullDate(label)}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color }}>{v}%</div>
      </div>
    );
  };

  const showChartSkeleton = useDelayedFlag(loading && !loadFailed);

  return (
    <>
      <PerfShell embedded={embedded} gamePage={game_id && true}>
        {slug && <GameSideBar slug={slug} />}
        {/* House-rule page title (desktop only). Mobile keeps the header's own
            title; md:pt-10 matches the old title's position so content still
            clears the fixed top nav. */}
        {pageTitle && !embedded && (
          <SettingsPageHeading
            title={pageTitle}
            className="hidden md:block px-1 md:pt-10 pb-3"
          />
        )}
        {/* pb-20 on mobile clears the fixed bottom nav (h-16 = 64px) with a
            small gap, so the chart's x-axis isn't hidden behind it — without the
            extra dead space pb-24 left. */}
        <div className="chart-container flex flex-col pb-20 md:pb-4">
          {/* C6: inline range selector (active = dark text + green underline).
              Hidden in the empty state so the message starts right under the
              profile tabs, on the same line as the Holdings / Trade History /
              Posts empty states (per design feedback). */}
          {!isEmpty && (
            <div className="flex items-center gap-6 border-b border-[#F3F4F6] px-1 pt-2">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setSelectedRange(r.key)}
                  className={`pb-2 text-[15px] font-medium border-b-2 -mb-px transition-colors ${
                    selectedRange === r.key
                      ? "text-[#1A1A1A] border-[#35b23c]"
                      : "text-[#9CA3AF] border-transparent hover:text-[#1A1A1A]"
                  }`}
                >
                  {r.key}
                </button>
              ))}
            </div>
          )}

          {/* C9: definite height so the chart fills the panel (taller in the
              full tab, shorter inside the modal). ResponsiveContainer needs a
              concrete parent height. SAME height for empty + loading + chart so
              the frame/selector never jump — only the line/curve/text change
              (the loading phase renders this same frame at 0–50%). */}
          <div
            /* tabular-nums: force equal-width figures so the Y-axis %s line up in
               a neat column (SVG <text> inherits font-variant-numeric). With the
               default proportional digits, "1" is narrower than "6", so the
               right-anchored labels shifted left as the digit grew and pushed the
               leading minus sign off the edge — that was the "minus gradually
               disappears" effect. */
            className={`w-full pt-3 tabular-nums ${
              embedded && props.minHeight !== false ? "h-[52vh]" : "h-[48vh]"
            }`}
          >
            {loadFailed ? (
              // Section error: the rest of the profile stays visible and usable.
              <div className="flex h-full items-center justify-center">
                <SectionError
                  message="Performance data could not be loaded"
                  onRetry={() => setReloadKey((k) => k + 1)}
                />
              </div>
            ) : showChartSkeleton ? (
              // Chart rectangle at the panel's real height. Deliberately NOT the
              // live AreaChart with an empty dataset — that drew a Y axis and a
              // green line before any data existed, which reads as a real flat
              // record.
              <SkeletonChart height="100%" />
            ) : loading ? null : isEmpty ? (
              // Empty state = the real chart frame (Y-axis %s + gridlines + a low
              // decorative curve) with the copy centered on top, so it reads as a
              // chart that hasn't filled in yet. Two states — "building" once a
              // trade is placed (hasTraded) vs "no trades yet" — private (owner
              // CTA) vs public copy.
              (() => {
                const copy = performanceEmptyCopy({
                  surface: "performance",
                  building: hasTraded,
                  isSelf: isSelfProfile,
                });
                return (
                  <div className="relative h-full w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={emptyCurveData(hasTraded)}
                        margin={{ top: 16, right: 16, left: 0, bottom: 14 }}
                      >
                        <defs>
                          <linearGradient
                            id="perfEmptyChartFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#35b23c" stopOpacity={0.16} />
                            <stop offset="100%" stopColor="#35b23c" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="#EEF0F2" />
                        {/* Inset from the plot edges so the start dot (on the
                            first point) isn't half-clipped by the plot-area clip. */}
                        <XAxis hide dataKey="x" padding={{ left: 14, right: 8 }} />
                        <YAxis
                          domain={[0, 50]}
                          ticks={[0, 10, 20, 30, 40, 50]}
                          tickFormatter={(v) => `${v}%`}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#9CA3AF" }}
                          width={44}
                        />
                        <Area
                          type="monotone"
                          dataKey="v"
                          stroke="#35b23c"
                          strokeWidth={2.5}
                          fill="url(#perfEmptyChartFill)"
                          dot={<StartDot />}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    {/* copy + owner CTA start right under the tabs (the range
                        selector is hidden when empty), matching the spacing of
                        the Holdings / Trade History / Posts empty states. */}
                    <div className="absolute inset-0 flex flex-col items-center justify-start pt-3 text-center pl-12 pr-4 md:px-6">
                      {/* No icon here (the curve is the illustration), but leave
                          the same vertical space the other tabs' icon occupies
                          (w-14 h-14 + mb-4) so the title lines up with theirs. */}
                      <div className="h-14 mb-4 shrink-0" aria-hidden="true" />
                      <p className="text-lg md:text-xl font-bold text-gray-900 max-w-[340px] [text-wrap:balance]">
                        {copy.title}
                      </p>
                      {copy.body && (
                        <p className="text-sm text-gray-500 mt-1.5 max-w-[320px] [text-wrap:balance]">
                          {copy.body}
                        </p>
                      )}
                      {copy.cta && isSelfProfile && (
                        <button
                          type="button"
                          onClick={() =>
                            // /<slug>/trade inside a league — the revamped Trade
                            // screen scoped to it. /<slug>/charts is the
                            // pre-revamp one. See NewPortfolioSection.
                            navigate(slug ? `/${slug}/trade` : "/charts")
                          }
                          className="mt-4 px-5 py-2.5 rounded-lg bg-primaryColor text-white font-semibold font-poppins"
                        >
                          {copy.cta}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={performanceData}
                margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
              >
                <defs>
                  {/* C8: gradient fill follows the line color, quiet opacity, and
                      anchors to the line — strongest at the line, transparent at
                      the 0% baseline, on both sides of zero. */}
                  <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity={fillTopOpacity} />
                    <stop offset={zeroOffsetPct} stopColor={lineColor} stopOpacity={0} />
                    <stop offset="100%" stopColor={lineColor} stopOpacity={fillBottomOpacity} />
                  </linearGradient>
                </defs>
                {/* C7: faint solid horizontal guides only, no dashes, no box */}
                <CartesianGrid vertical={false} stroke="#EEF0F2" />
                <XAxis
                  dataKey="date"
                  ticks={xTicks}
                  tickFormatter={fmtMonthYear}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9CA3AF" }}
                  minTickGap={20}
                />
                {/* width 56 (was 44): the labels are right-anchored, so a too-narrow
                    axis pushed the leading minus of the negative %s ("-0.06%") past
                    the left SVG edge and clipped it. 56 keeps the whole label,
                    minus sign included, inside the viewport. */}
                <YAxis
                  domain={[yMin, yMax]}
                  ticks={yTicks}
                  tickFormatter={(v) => `${v}%`}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9CA3AF" }}
                  width={56}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                />
                {/* C7: 0% baseline a touch stronger than the guides */}
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1.5} />
                {/* C3/C4/C8: single-color line, gradient fill, no resting dots */}
                <Area
                  type="monotone"
                  dataKey="performance"
                  stroke={lineColor}
                  strokeWidth={2.5}
                  fill="url(#perfFill)"
                  baseValue={0}
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: lineColor,
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </PerfShell>
    </>
  );
};

export default PerformanceSection;
